import { Component, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseComponent } from 'app/components/base.component';
import { ToastrService } from 'ngx-toastr';
import { BsModalService } from 'ngx-bootstrap';
import { ModalType } from 'app/components/modals/modal.const';
import { FormFieldType } from 'app/components/modals/modal-form-template/modal-form.const';
import {
  GetQCCategoryQuery,
  GetQCKeywordQuery,
  QCCategory,
  QCKeyword,
  SmartQcService,
} from 'app/common/services/SmartQC.service';
import {
  buildCreateQCKeywordModel,
  buildCreateQCKeywordPayload,
  buildEditQCKeywordModel,
  buildEditQCKeywordPayload,
  buildTableColumns,
  FormFieldCode,
} from './setting-keyword.util';
import { TableAction } from 'app/components/table-component/table.const';

type ModalAction = 'add' | 'edit' | 'close';

@Component({
  selector: 'app-smart-qc-setting-keyword',
  templateUrl: './setting-keyword.component.html',
  styleUrls: ['./setting-keyword.component.css'],
  providers: [SmartQcService],
})
export class SettingKeywordComponent extends BaseComponent {
  @ViewChild('template') template: ElementRef;
  public loading: boolean = false;
  public qcKeywords: QCKeyword[] = [];
  private _currentQCKeyword: QCKeyword = null;
  public keyword: string = '';
  public currentPage: number = 1;
  public pageSize: number = 10;
  public totalPage: number = 0;
  public totalItems: number = 0;
  public modalAction: ModalAction = 'close';
  public tableColumns: any[] = [];

  constructor(
    protected changeDetector: ChangeDetectorRef,
    protected toastr: ToastrService,
    protected modalService: BsModalService,
    private smartQCService: SmartQcService
  ) {
    super(changeDetector, toastr, modalService);
  }

  ngOnInit(): void {
    this.tableColumns = buildTableColumns();
    this.getQCKeywords();
  }

  getQCKeywords() {
    this.loading = true;

    const query: GetQCKeywordQuery = {
      page: this.currentPage,
      page_size: this.pageSize,
    };

    const code = this.keyword.trim();
    if (code) {
      query.code = code;
    }

    return this.smartQCService.getQCKeywords(query).subscribe(
      ({ data }) => {
        this.loading = false;
        this.qcKeywords = data.data;
        this.totalItems = data.total;
        this.totalPage = Math.ceil(data.total / this.pageSize);
      },
      error => {
        this.loading = false;
        this.qcKeywords = [];
        this.throwError(error.message);
      }
    );
  }

  initModal() {
    this.modelContent = {
      type: ModalType.Primary,
      title: this.modalAction === 'add' ? 'Thêm mới từ khóa' : 'Cập nhật từ khóa',
      submit_title: this.modalAction === 'add' ? 'Thêm mới' : 'Cập nhật',
      close_title: 'Hủy bỏ',
    };

    this.modelDisplay = [
      {
        key: FormFieldCode.CODE,
        label: 'Tên',
        placeholder: 'Nhập tên từ khóa',
        type: FormFieldType.Text,
        value: this.model[FormFieldCode.CODE],
        required: true,
        disabled: false,
        width: 'full',
        order: 0,
      },
      {
        key: FormFieldCode.CATEGORY_IDs,
        label: 'Danh mục',
        placeholder: 'Danh mục',
        type: FormFieldType.InfiniteSelect,
        value: this.model[FormFieldCode.CATEGORY_IDs],
        required: false,
        disabled: false,
        width: 'full',
        order: 1,
        options: {
          pageSize: 10,
          asyncSearch: true,
          select2Options: {
            multiple: true,
            placeholder: 'Chọn danh mục',
          },
          asyncFetch: (query: GetQCCategoryQuery) =>
            this.smartQCService.getQCCategories(query).map(({ data }) => {
              const options = data.data.map((item: QCCategory) => ({
                id: item._id,
                text: item.name,
              }));

              return {
                options,
                totalItems: data.total,
              };
            }),
        },
      },
    ];
  }

  handleSearch() {
    const keyword = this.keyword.trim();
    if (!keyword) {
      this.throwError('Nhập thông tin để tìm kiếm');
      return;
    }

    this.currentPage = 1;
    this.getQCKeywords();
  }

  handleCloseModal() {
    this.modalAction = 'close';
    this.closeModal();
  }

  handleOpenModal(modalAction: ModalAction) {
    this.modalAction = modalAction;
    this.model = modalAction === 'add' ? buildCreateQCKeywordModel() : buildEditQCKeywordModel(this._currentQCKeyword);

    this.initModal();

    this.openModal(
      this.template,
      {
        backdrop: true,
        ignoreBackdropClick: true,
        class: 'modal-xl',
        keyboard: false,
      },
      {},
      {
        modelContent: this.modelContent,
        modelDisplay: this.modelDisplay,
      }
    );
  }

  handleTableAction(event: TableAction, qcKeyword: QCKeyword) {
    if (event === TableAction.edit) {
      this._currentQCKeyword = qcKeyword;
      this.handleOpenModal('edit');
    } else if (event === TableAction.delete) {
      const okDeleteQCKeyword = confirm('Bạn có muốn xóa từ khóa này không?');
      if (okDeleteQCKeyword) {
        this.smartQCService.deleteQCKeyword(qcKeyword._id).subscribe(
          () => {
            this.throwSuccess('Xóa thành công');
            this.getQCKeywords();
          },
          error => {
            this.throwError(error.message);
          }
        );
      }
    }
  }

  async handleSubmit(model: any) {
    let observable: Observable<any>;

    if (this.modalAction === 'add') {
      observable = this.smartQCService.createQCKeyword(buildCreateQCKeywordPayload(model));
    } else if (this.modalAction === 'edit') {
      observable = this.smartQCService.updateQCKeyword(
        this._currentQCKeyword._id,
        buildEditQCKeywordPayload(model, this._currentQCKeyword)
      );
    }

    observable.subscribe(
      () => {
        const message = this.modalAction === 'add' ? 'Thêm mới thành công' : 'Cập nhật thành công';

        this.throwSuccess(message);
        this.getQCKeywords();
        this.handleCloseModal();
      },
      error => {
        this.message = {
          class: 'alert dark alert-danger',
          content: error.message,
        };

        setTimeout(() => {
          this.message = null;
        }, 3000);
      }
    );
  }

  async onChangeValueModel(_fieldCode: FormFieldCode, qcKeyword: any) {
    this.model = qcKeyword;
  }

  setPage(page: number) {
    let newPage: number = page;
    if (page < 1) {
      newPage = 1;
    }

    if (page > this.totalPage) {
      newPage = this.totalPage;
    }

    if (newPage !== this.currentPage) {
      this.currentPage = newPage;
      this.getQCKeywords();
    }
  }

  goPage2(page: string) {
    let toPage: any = page;
    if (!toPage) {
      toPage = 1;
    }

    if (Number.isInteger(toPage) && toPage <= 0) {
      toPage = 1;
    }

    this.setPage(toPage);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/admin/icontact/smart-qc/setting/keyword/setting-keyword.component.ts